import { createElement, Fragment } from 'react';
import { sanitizeConfirmMessage } from './confirmMessage.js';

/**
 * Renders a ConfirmModal message as React elements (review finding H7).
 *
 * See `confirmMessage.js` for why this exists. The important property is that
 * this is the ONLY thing that turns a message into UI, it creates elements by
 * name from a fixed allowlist, and React escapes every text node — so there
 * is no `innerHTML` anywhere in the path.
 */

/** Elements that must be created with no children argument at all. */
const VOID_TAGS = new Set(['br']);

/**
 * @param {import('./confirmMessage.js').ConfirmNode} node
 * @param {string} key
 * @returns {import('react').ReactNode}
 */
function toElement(node, key) {
  if (typeof node === 'string') return node;

  /** @type {Record<string, unknown>} */
  const props = { key };
  if (node.style) props.style = node.style;
  if (node.className) props.className = node.className;

  if (VOID_TAGS.has(node.tag)) return createElement(node.tag, props);

  const children = (node.children ?? []).map((child, index) => toElement(child, `${key}.${index}`));
  return createElement(node.tag, props, children.length > 0 ? children : null);
}

/**
 * @param {{ message: unknown }} props
 */
export default function ConfirmMessage({ message }) {
  const nodes = sanitizeConfirmMessage(message);
  return createElement(
    Fragment,
    null,
    nodes.map((node, index) => toElement(node, `m${index}`))
  );
}
